import type { TextClip } from '../../format/types';
import { useTimeline } from '../../state/timeline';
import { hexToRgba, rgbaToHex } from './colorutils';
import { Num, Slider } from './fields';
import ParamSlider from './ParamSlider';

export default function TextClipInspector({
  clip,
  onSeek,
}: {
  clip: TextClip;
  onSeek?: (t: number) => void;
}) {
  const update = useTimeline((s) => s.updateClip);
  const patch = (p: Partial<TextClip>) => update(clip.id, p);
  const patchStyle = (p: Partial<TextClip['style']>) =>
    update(clip.id, { style: { ...clip.style, ...p } });
  const s = clip.style;

  return (
    <>
      <div className="insp-section">
        <h3>Text</h3>
        <textarea
          rows={3}
          value={clip.text}
          onChange={(e) => patch({ text: e.target.value })}
        />
        <Num label="start" value={clip.start} min={0} onChange={(v) => patch({ start: Math.max(0, v) })} />
        <Num
          label="duration"
          value={clip.duration}
          min={0.1}
          onChange={(v) => patch({ duration: Math.max(0.1, v) })}
        />
      </div>

      <div className="insp-section">
        <h3>Style</h3>
        <label className="insp-field">
          <span>font</span>
          <select
            value={s.fontFamily}
            onChange={(e) => patchStyle({ fontFamily: e.target.value as TextClip['style']['fontFamily'] })}
          >
            <option value="sans">sans</option>
            <option value="serif">serif</option>
            <option value="mono">mono</option>
          </select>
        </label>
        <Slider
          label="size"
          value={s.fontSize}
          min={0.02}
          max={0.25}
          step={0.005}
          onChange={(v) => patchStyle({ fontSize: v })}
        />
        <label className="insp-field">
          <span>color</span>
          <input type="color" value={s.color} onChange={(e) => patchStyle({ color: e.target.value })} />
        </label>
        <label className="insp-field">
          <span>bold</span>
          <input
            type="checkbox"
            checked={s.bold}
            onChange={(e) => patchStyle({ bold: e.target.checked })}
          />
        </label>
        <label className="insp-field">
          <span>italic</span>
          <input
            type="checkbox"
            checked={s.italic}
            onChange={(e) => patchStyle({ italic: e.target.checked })}
          />
        </label>
        <label className="insp-field">
          <span>align</span>
          <select
            value={s.align}
            onChange={(e) => patchStyle({ align: e.target.value as TextClip['style']['align'] })}
          >
            <option value="left">left</option>
            <option value="center">center</option>
            <option value="right">right</option>
          </select>
        </label>
        <label className="insp-field">
          <span>background</span>
          <input
            type="checkbox"
            checked={s.background !== ''}
            onChange={(e) => patchStyle({ background: e.target.checked ? 'rgba(0,0,0,0.5)' : '' })}
          />
          {s.background !== '' && (
            <input
              type="color"
              value={rgbaToHex(s.background)}
              onChange={(e) => patchStyle({ background: hexToRgba(e.target.value, 0.5) })}
            />
          )}
        </label>
        <label className="insp-field">
          <span>outline</span>
          <input
            type="checkbox"
            checked={s.outline !== ''}
            onChange={(e) => patchStyle({ outline: e.target.checked ? '#000000' : '' })}
          />
          {s.outline !== '' && (
            <input
              type="color"
              value={s.outline}
              onChange={(e) => patchStyle({ outline: e.target.value })}
            />
          )}
        </label>
      </div>

      <div className="insp-section">
        <h3>Position</h3>
        <ParamSlider clip={clip} path="position.x" param={clip.position.x} label="x"
          min={0} max={1} step={0.01}
          onStatic={(v) => patch({ position: { ...clip.position, x: v } })} onSeek={onSeek} />
        <ParamSlider clip={clip} path="position.y" param={clip.position.y} label="y"
          min={0} max={1} step={0.01}
          onStatic={(v) => patch({ position: { ...clip.position, y: v } })} onSeek={onSeek} />
      </div>

      <div className="insp-section">
        <h3>Fades</h3>
        <Num label="fade in" value={clip.fadeIn} min={0} onChange={(v) => patch({ fadeIn: Math.max(0, v) })} />
        <Num
          label="fade out"
          value={clip.fadeOut}
          min={0}
          onChange={(v) => patch({ fadeOut: Math.max(0, v) })}
        />
      </div>
    </>
  );
}
